import React, { ReactNode } from "react";

import styled from "@emotion/styled";
import { FlexFrame, MainColumn } from "../core/BodyLayout";
import { flexHorizontalCenter } from "src/design-system/styles/layout";
import { LandingFooter } from "../footer/LandingFooter";
import { useTrackScroll } from "src/hooks/scroll";
import { BelowFold } from "../below-fold/BelowFold";
import { trackEvent } from "src/utils/api/tracker";
import { scrollToTop } from "src/utils/scroll";
import { getURLParams } from "src/utils/urlParams";
import Button from "src/design-system/components/Button/Button";
import {
  breakpointDesktopOnly,
  breakpointWideDesktopOnly,
} from "src/design-system/styles/breakpoints";
import i18n from "src/locales";
import { BenefitsExpanded } from "../below-fold/BenefitsExpanded";
import PromoBanner from "./PromoBanner";
import { HolidayPromoBanner } from "src/components/banner/HolidayPromoBanner";
import { isHolidayPromotion } from "src/utils/holidayPromo";
import { Header } from "../header/Header";
import { ClinicianReferralBanner } from "../banner/ClinicianReferralBanner";
import { isAffiliateTrial30PercentOff } from "@utils/userSegment/features";
import { isEligibleFor21DayTrial } from "src/utils/plans";
import { AffiliateTrialBanner } from "../banner/AffiliateTrialBanner";

type Props = {
  children: ReactNode;
  landingEntryKeyword?: string;
};

export const SurveyPageContent = styled.div`
  ${flexHorizontalCenter}
  position: relative;
  flex: 1;
`;

const CTAButton = styled(Button)`
  @media ${breakpointDesktopOnly} {
    width: 400px;
  }
`;

const CTAText = styled.div`
  font-family: "Untitled Serif";
  font-style: normal;
  font-weight: 400;
  font-size: 20px;
  line-height: 23px;
  margin-bottom: 32px;
  text-align: center;
  margin-bottom: 24px;
  @media ${breakpointWideDesktopOnly} {
    font-size: 36px;
    line-height: 39px;
    margin-bottom: 32px;
  }
`;

const DesktopLineBreak = styled.br`
  display: none;
  @media ${breakpointWideDesktopOnly} {
    display: inline-block;
  }
`;

// Forces the landing page flex frame hosting the survey question content to
// take up the full height of the window thus ensuring the content afterwards comes below the fold
const FullHeightFlexFrame = styled(FlexFrame)`
  min-height: 100vh;
`;

export const shouldShowPromoBanner = () => {
  const urlParams = getURLParams();
  return (
    urlParams.showBanner === "true" &&
    urlParams.bannerCode &&
    urlParams.bannerPcOff
  );
};

const BelowFoldComponent = ({
  keyword,
  scrollToTopWithEventTracking,
}: {
  keyword?: string;
  scrollToTopWithEventTracking: () => void;
}) => {
  if (keyword) {
    return (
      <BenefitsExpanded keyword={keyword}>
        <CTAText>
          {i18n.t(`landingSurveyPage:maximizeResults:${keyword}`)}
          <DesktopLineBreak />
          {i18n.t("landingSurveyPage:finishQuiz")}
        </CTAText>
        <CTAButton onClick={scrollToTopWithEventTracking}>
          {i18n.t("belowFold:benefits:cta")}
        </CTAButton>
      </BenefitsExpanded>
    );
  }
  return (
    <BelowFold>
      <CTAButton onClick={scrollToTopWithEventTracking}>
        {i18n.t("belowFold:benefits:cta")}
      </CTAButton>
    </BelowFold>
  );
};

export default function LandingSurveyPage({
  children,
  landingEntryKeyword,
}: Props) {
  useTrackScroll("ScrolledFromTop");

  function scrollToTopWithEventTracking() {
    scrollToTop();
    trackEvent("ClickedLandingPageBottomCTA");
  }

  const urlParams = getURLParams();
  const { bannerCode } = urlParams;
  const bannerPcOff = parseInt(urlParams.bannerPcOff, 10);

  return (
    <>
      <FullHeightFlexFrame>
        {isHolidayPromotion() && <HolidayPromoBanner />}
        {isAffiliateTrial30PercentOff() && <ClinicianReferralBanner />}
        {isEligibleFor21DayTrial() && <AffiliateTrialBanner />}
        <MainColumn maxWidth={400}>
          <Header pill={false} />
          {shouldShowPromoBanner() && (
            <PromoBanner promoCode={bannerCode} percentageOff={bannerPcOff} />
          )}
          <SurveyPageContent id="slide">{children}</SurveyPageContent>
        </MainColumn>
      </FullHeightFlexFrame>
      <BelowFoldComponent
        keyword={landingEntryKeyword}
        scrollToTopWithEventTracking={scrollToTopWithEventTracking}
      />
      <LandingFooter />
    </>
  );
}
